import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { DownloadSimpleIcon as DownloadSimple, ShareNetworkIcon as ShareNetwork, CopySimpleIcon as CopySimple, TrashIcon as Trash, MegaphoneIcon as Megaphone, FileVideoIcon as FileVideo, TextAlignLeftIcon as TextAlignLeft, PackageIcon as Package, ClockCounterClockwiseIcon as ClockCounterClockwise, CheckCircleIcon as CheckCircle, WarningIcon as Warning, ArrowClockwiseIcon as ArrowClockwise, CircleNotchIcon as CircleNotch, ArrowSquareOutIcon as ArrowSquareOut, FilmStripIcon as FilmStrip } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

/**
 * Admin marketing kit library.
 *
 * Surfaces /app/marketing/ — six category groups (videos, scripts,
 * captions, storyboards, kit, other). Each item:
 *   • Preview (markdown excerpt or inline <video> for .mp4/.webm)
 *   • Download button (admin-auth, streams the file)
 *   • Share button (creates a public token-protected link valid for
 *     1h–30d; admin pastes it into Slack / email / SMS)
 *
 * Plus: "Download everything" zip + a Shares panel showing every
 * active token, hit count, expiry, and revoke action.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  videos: { label: "Product demo videos", icon: <FileVideo size={18} weight="duotone" /> },
  scripts: { label: "Voiceover scripts", icon: <TextAlignLeft size={18} weight="duotone" /> },
  storyboards: { label: "Storyboards", icon: <TextAlignLeft size={18} weight="duotone" /> },
  captions: { label: "Social captions", icon: <Megaphone size={18} weight="duotone" /> },
  kit: { label: "Outreach kit", icon: <Package size={18} weight="duotone" /> },
  other: { label: "Reference docs", icon: <TextAlignLeft size={18} weight="duotone" /> },
};

export default function AdminMarketing() {
  const [data, setData] = useState(null);
  const [clips, setClips] = useState([]);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareTarget, setShareTarget] = useState(null); // {path, label, isBundle}
  const [shareHours, setShareHours] = useState(168);
  const [shareLabel, setShareLabel] = useState("");
  const [creatingShare, setCreatingShare] = useState(false);
  const [recordJob, setRecordJob] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [a, c, s, j] = await Promise.all([
        api.get("/admin/marketing/assets"),
        api.get("/admin/marketing/clips"),
        api.get("/admin/marketing/shares"),
        api.get("/admin/marketing/rerecord/status"),
      ]);
      setData(a.data);
      setClips(c.data?.clips || []);
      setShares(s.data?.shares || []);
      setRecordJob(j.data?.job || null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  // Poll the rerecord job while it's running. Once it transitions to a
  // terminal state we refresh the asset list so the new clips are
  // surfaced immediately.
  useEffect(() => {
    if (!recordJob) return;
    if (recordJob.status !== "running" && recordJob.status !== "queued") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get("/admin/marketing/rerecord/status");
        const next = r.data?.job || null;
        setRecordJob(next);
        if (next && (next.status === "succeeded" || next.status === "failed")) {
          clearInterval(t);
          if (next.status === "succeeded") {
            toast.success("Videos re-recorded successfully.");
            const a = await api.get("/admin/marketing/assets");
            setData(a.data);
          } else {
            toast.error("Re-record job failed — see the log tail above.");
          }
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(t);
  }, [recordJob?.job_id, recordJob?.status]);

  const startRerecord = async () => {
    if (!confirm(
      "Re-record all 6 demo videos?\n\nThis takes ~1–3 minutes. The page will keep polling for status. Existing files are overwritten."
    )) return;
    try {
      const r = await api.post("/admin/marketing/rerecord");
      if (r.data?.already_running) {
        toast.info("A re-record job is already in flight.");
      } else {
        toast.success("Re-record started. Watch the status pill above.");
      }
      const s = await api.get("/admin/marketing/rerecord/status");
      setRecordJob(s.data?.job || null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadAsset = async (path, format) => {
    try {
      const url = format
        ? `/admin/marketing/download?path=${encodeURIComponent(path)}&format=${format}`
        : `/admin/marketing/download?path=${encodeURIComponent(path)}`;
      const r = await api.get(url, { responseType: "blob" });
      const base = path.split("/").pop();
      const stem = base.replace(/\.[^.]+$/, "");
      const filename = format ? `${stem}.${format}` : base;
      _saveBlob(r.data, filename);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadBundle = async () => {
    try {
      const r = await api.get(`/admin/marketing/bundle.zip`, { responseType: "blob" });
      _saveBlob(r.data, `armstrong-marketing-kit.zip`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadClipZip = async (clipId) => {
    try {
      const r = await api.get(`/admin/marketing/clip/${clipId}.zip`, { responseType: "blob" });
      _saveBlob(r.data, `clip-${clipId}.zip`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openShareDialog = (target) => {
    setShareTarget(target);
    setShareLabel(target.label || target.path || "");
    setShareHours(168);
  };

  const createShare = async () => {
    if (!shareTarget) return;
    setCreatingShare(true);
    try {
      const r = await api.post(`/admin/marketing/share`, {
        path: shareTarget.isBundle ? "bundle.zip" : shareTarget.path,
        expires_in_hours: Number(shareHours),
        label: shareLabel,
      });
      const url = `${BACKEND}${r.data.api_path}`;
      try { await navigator.clipboard?.writeText(url); } catch {}
      toast.success("Share link created and copied to clipboard.");
      setShareTarget(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreatingShare(false);
    }
  };

  const revoke = async (token) => {
    if (!confirm("Revoke this link? Anyone holding it will get a 404.")) return;
    try {
      await api.delete(`/admin/marketing/share/${token}`);
      toast.success("Link revoked.");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const stats = useMemo(() => {
    if (!data) return null;
    return {
      count: data.total_count,
      size: data.total_size_human,
      videos: data.categories?.videos?.length || 0,
    };
  }, [data]);

  return (
    <AppShell>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">Admin · Marketing</p>
          <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">Marketing kit</h1>
          <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl">
            Product demo videos, voiceover scripts, social captions, outreach
            email templates, and storyboards. Download individual assets,
            grab the whole kit as a zip, or generate shareable public links
            for partners and your video editor.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={startRerecord}
            disabled={recordJob?.status === "running" || recordJob?.status === "queued"}
            data-testid="marketing-rerecord-btn"
            variant="outline"
            className="rounded-full h-10 px-5 border-[#E5E5E5]"
          >
            {(recordJob?.status === "running" || recordJob?.status === "queued") ? (
              <>
                <CircleNotch size={14} weight="bold" className="mr-1.5 animate-spin" />
                Recording…
              </>
            ) : (
              <>
                <ArrowClockwise size={14} weight="bold" className="mr-1.5" />
                Re-record videos
              </>
            )}
          </Button>
          <Button
            onClick={downloadBundle}
            data-testid="marketing-download-bundle"
            className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full h-10 px-5"
          >
            <DownloadSimple size={14} weight="bold" className="mr-1.5" /> Download everything (.zip)
          </Button>
          <Button
            onClick={() => openShareDialog({ path: "bundle.zip", label: "Full marketing kit", isBundle: true })}
            data-testid="marketing-share-bundle"
            variant="outline"
            className="rounded-full h-10 px-5 border-[#E5E5E5]"
          >
            <ShareNetwork size={14} weight="bold" className="mr-1.5" /> Share full kit
          </Button>
        </div>
      </div>

      {recordJob && (
        <RerecordJobPanel job={recordJob} />
      )}

      {stats && (
        <div className="bg-[#0E0E0E] text-white rounded-2xl p-5 mb-8 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="marketing-stats">
          <Stat label="Files" value={stats.count} />
          <Stat label="Total size" value={stats.size} />
          <Stat label="Videos" value={stats.videos} />
          <Stat label="Active shares" value={shares.filter((s) => !s.expired).length} />
        </div>
      )}

      {/* Live in-platform reels — recordable URLs that always reflect
          the current product UI. The recording crop should be 1080×1920
          (vertical / IG / TikTok) or 1920×1080 (horizontal / YouTube).
          Both reels auto-loop with a 0.6s ink hold so screen recordings
          can be trimmed cleanly to one 15s cycle. */}
      <section
        className="mb-10 rounded-2xl border border-[#E2DAC2] bg-cream p-6"
        data-testid="marketing-live-reels"
      >
        <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-gold-dark mb-1.5">
              Evergreen
            </p>
            <h2 className="font-spectral text-2xl tracking-tight">Live in-platform reels</h2>
            <p className="text-sm text-[#6B6B6B] mt-1 max-w-2xl">
              Two locked-ratio 15-second reels rendered live from the platform
              UI itself — they update automatically when the brand evolves.
              Open a reel, set your screen recorder to the listed crop, and
              capture one full loop.
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <ReelCard
            label="Buyer reel"
            kicker="Your next family member"
            path="/marketing/reel/buyer"
            data-testid="reel-card-buyer"
          />
          <ReelCard
            label="Breeder reel"
            kicker="Run your kennel like a brand"
            path="/marketing/reel/seller"
            data-testid="reel-card-seller"
          />
        </div>
        <p className="text-[12px] text-[#6B6B6B] mt-4 leading-relaxed">
          <strong>Recording tip:</strong> append <code className="font-mono">?clean=1</code> to
          hide the dimension marker before you hit record. Use <code className="font-mono">?o=horizontal</code>
          for the 1920×1080 YouTube/landing-page version. <code className="font-mono">?paused=1&t=0.5</code>
          freezes a beat so you can capture stills for press.
        </p>
      </section>


      {loading && (
        <div className="space-y-4">
          {[0, 1].map((i) => <Skeleton key={i} className="h-48 w-full rounded-xl" />)}
        </div>
      )}

      {!loading && clips.length > 0 && (
        <section className="mb-12" data-testid="marketing-clips-section">
          <div className="flex items-end justify-between gap-3 mb-3">
            <div>
              <h2 className="font-spectral text-2xl tracking-tight">Clip cards</h2>
              <p className="text-sm text-[#6B6B6B] mt-1 max-w-2xl">
                Each card pairs one product video with the script, storyboard
                section, and social caption it goes with. The
                <strong> "Download clip folder"</strong> button gives you a
                self-contained zip — drop one folder into your video editor
                and you have everything for that spot in one place.
              </p>
            </div>
          </div>
          <div className="bg-[#FBF7EC] border border-[#E8D9A6] text-[#8A6A00] rounded-lg p-3 text-[12px] mb-4 leading-relaxed">
            <strong>Editor compatibility:</strong> Each clip's <code className="font-mono">video.mp4</code>
            is H.264 high@4.0 with a silent AAC track and <code>+faststart</code> —
            iMovie, Premiere Pro, Final Cut, DaVinci Resolve, and CapCut will
            all import it natively. The <code className="font-mono">video.webm</code>
            is the original Playwright recording (smaller, browser-native).
            <br />
            <strong>Reading the docs:</strong> every README / script /
            storyboard / caption ships in <code>.md</code> (markdown source),
            <code>.txt</code> (double-click to open in TextEdit / Notepad),
            <code>.html</code> (double-click to open in any browser), and
            on <code>README</code> / <code>INDEX</code> also <code>.pdf</code>
            (best for sharing with non-technical reviewers). Pick whichever
            your team prefers — <code>.txt</code> works on every OS with no
            extra app.
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {clips.map((c) => (
              <ClipCard
                key={c.id}
                clip={c}
                onDownloadClipZip={() => downloadClipZip(c.id)}
                onDownloadAsset={downloadAsset}
              />
            ))}
          </div>
        </section>
      )}

      {!loading && data && Object.entries(data.categories).map(([catKey, items]) => {
        if (!items || items.length === 0) return null;
        const meta = CATEGORY_META[catKey] || CATEGORY_META.other;
        return (
          <section key={catKey} className="mb-10" data-testid={`marketing-cat-${catKey}`}>
            <div className="flex items-center gap-2 mb-4">
              <span className="h-9 w-9 rounded-lg bg-[#FBF7EC] text-gold flex items-center justify-center">
                {meta.icon}
              </span>
              <h2 className="font-spectral text-2xl tracking-tight">{meta.label}</h2>
              <span className="text-xs text-[#6B6B6B]">· {items.length}</span>
            </div>
            <div className={catKey === "videos" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
              {items.map((it) => (
                <AssetCard
                  key={it.path}
                  item={it}
                  onDownload={downloadAsset}
                  onShare={(t) => openShareDialog({ path: it.path, label: it.name, isBundle: false })}
                />
              ))}
            </div>
          </section>
        );
      })}

      {/* ---- Shares panel ---- */}
      <section className="mt-12" data-testid="marketing-shares-panel">
        <h2 className="font-spectral text-2xl tracking-tight mb-4 flex items-center gap-2">
          <ClockCounterClockwise size={22} weight="duotone" className="text-gold" />
          Active share links
          {shares.length > 0 && <span className="text-xs text-[#6B6B6B]">· {shares.length}</span>}
        </h2>
        {shares.length === 0 ? (
          <div className="text-sm text-[#6B6B6B] bg-white border border-dashed border-[#E5E5E5] rounded-xl p-6 text-center">
            No share links yet. Use <strong>Share full kit</strong> or the share button on any asset to create one.
          </div>
        ) : (
          <div className="space-y-2">
            {shares.map((s) => <ShareRow key={s.token} share={s} onRevoke={revoke} />)}
          </div>
        )}
      </section>

      {/* ---- Create-share dialog ---- */}
      <Dialog open={!!shareTarget} onOpenChange={(o) => !o && setShareTarget(null)}>
        <DialogContent className="max-w-md" data-testid="marketing-share-dialog">
          <DialogHeader>
            <DialogTitle>Create shareable link</DialogTitle>
            <DialogDescription>
              The link is public — anyone with the URL can download until it expires.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#6B6B6B]">Asset</Label>
              <p className="text-sm font-mono mt-1 text-ink truncate">
                {shareTarget?.path || ""}
              </p>
            </div>
            <div>
              <Label htmlFor="share-label" className="text-xs uppercase tracking-[0.18em] text-[#6B6B6B]">
                Label (your reference)
              </Label>
              <Input
                id="share-label"
                value={shareLabel}
                onChange={(e) => setShareLabel(e.target.value)}
                placeholder="e.g. Sent to Jordan @ video editor"
                data-testid="marketing-share-label"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="share-hours" className="text-xs uppercase tracking-[0.18em] text-[#6B6B6B]">
                Expires in
              </Label>
              <select
                id="share-hours"
                value={shareHours}
                onChange={(e) => setShareHours(e.target.value)}
                data-testid="marketing-share-hours"
                className="mt-1 w-full rounded-lg border border-[#E5E5E5] p-2 text-sm bg-white"
              >
                <option value={1}>1 hour</option>
                <option value={24}>24 hours</option>
                <option value={72}>3 days</option>
                <option value={168}>7 days</option>
                <option value={336}>14 days</option>
                <option value={720}>30 days (max)</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareTarget(null)} className="rounded-full">
              Cancel
            </Button>
            <Button
              onClick={createShare}
              disabled={creatingShare}
              data-testid="marketing-share-create-btn"
              className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full"
            >
              {creatingShare ? "Creating…" : "Create & copy link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <p className="font-spectral text-2xl text-gold">{value}</p>
      <p className="text-[10px] uppercase tracking-[0.18em] text-white/60 mt-1">{label}</p>
    </div>
  );
}

function RerecordJobPanel({ job }) {
  const isRunning = job.status === "running" || job.status === "queued";
  const isOk = job.status === "succeeded";
  const isErr = job.status === "failed";
  const palette = isRunning
    ? { bg: "#FFF8E5", border: "#C9A227", fg: "#8A6A00", icon: <CircleNotch size={16} weight="bold" className="animate-spin" /> }
    : isOk
    ? { bg: "#F0F8F1", border: "#0F8043", fg: "#0F6B2F", icon: <CheckCircle size={16} weight="fill" /> }
    : isErr
    ? { bg: "#FCE9EA", border: "#C1272D", fg: "#7A1014", icon: <Warning size={16} weight="fill" /> }
    : { bg: "#F5F5F5", border: "#9CA3AF", fg: "#3F3F3F", icon: <ClockCounterClockwise size={16} weight="duotone" /> };
  const ts = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };
  return (
    <div
      className="mb-6 rounded-2xl border p-4"
      style={{ background: palette.bg, borderColor: `${palette.border}66`, color: palette.fg }}
      data-testid="marketing-rerecord-panel"
    >
      <div className="flex items-start gap-3">
        <span className="flex-shrink-0 mt-0.5">{palette.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">
            {isRunning && "Re-recording videos in progress…"}
            {isOk && "Videos refreshed"}
            {isErr && "Re-record job failed"}
            {job.status === "queued" && "Queued — starting…"}
          </p>
          <p className="text-[11px] mt-1 opacity-80">
            Job <span className="font-mono">{job.job_id}</span>
            {job.started_at && <> · Started {ts(job.started_at)}</>}
            {job.finished_at && <> · Finished {ts(job.finished_at)}</>}
            {job.exit_code != null && <> · Exit code {job.exit_code}</>}
          </p>
          {job.log_tail && (
            <pre
              className="mt-2 text-[10px] font-mono max-h-32 overflow-y-auto bg-black/5 rounded p-2 whitespace-pre-wrap"
              data-testid="marketing-rerecord-logtail"
            >
              {job.log_tail}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function AssetCard({ item, onDownload, onShare }) {
  const isVideo = item.mime?.startsWith("video/");
  const isMarkdown = item.path.toLowerCase().endsWith(".md");
  const tok = (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  const directUrl = `${BACKEND}/api/admin/marketing/download?path=${encodeURIComponent(item.path)}${isVideo ? `&token=${encodeURIComponent(tok)}` : ""}`;
  return (
    <article
      className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden hover:border-[#C9A227] transition-colors"
      data-testid={`marketing-asset-${item.path.replace(/[^a-z0-9]+/gi, "-")}`}
    >
      {isVideo && (
        <video
          controls
          preload="metadata"
          src={directUrl}
          className="w-full bg-black"
          data-testid={`marketing-video-${item.name}`}
        />
      )}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-mono text-[12px] font-semibold text-ink truncate" title={item.name}>
            {item.name}
          </h3>
          <span className="text-[10px] text-[#6B6B6B] flex-shrink-0">{item.size_human}</span>
        </div>
        {item.excerpt && (
          <p className="text-[12px] text-[#6B6B6B] leading-relaxed line-clamp-3 mb-3">
            {item.excerpt}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <Button
            size="sm"
            onClick={() => onDownload(item.path)}
            data-testid={`marketing-download-${item.name}`}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white h-8 px-3 text-[12px]"
          >
            <DownloadSimple size={12} weight="bold" className="mr-1" /> Download
          </Button>
          {isMarkdown && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDownload(item.path, "txt")}
                data-testid={`marketing-download-${item.name}-txt`}
                className="rounded-full border-[#E5E5E5] h-8 px-3 text-[12px]"
                title="Plain text — opens in TextEdit / Notepad"
              >
                .txt
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDownload(item.path, "html")}
                data-testid={`marketing-download-${item.name}-html`}
                className="rounded-full border-[#E5E5E5] h-8 px-3 text-[12px]"
                title="Styled HTML — opens in any browser"
              >
                .html
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDownload(item.path, "pdf")}
                data-testid={`marketing-download-${item.name}-pdf`}
                className="rounded-full border-[#E5E5E5] h-8 px-3 text-[12px]"
                title="PDF — opens in Preview / Adobe / any browser. Best for sharing with non-technical reviewers."
              >
                .pdf
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onShare(item.path)}
            data-testid={`marketing-share-${item.name}`}
            className="rounded-full border-[#E5E5E5] h-8 px-3 text-[12px]"
          >
            <ShareNetwork size={12} weight="bold" className="mr-1" /> Share
          </Button>
        </div>
      </div>
    </article>
  );
}

function ShareRow({ share, onRevoke }) {
  const url = `${BACKEND}/api/marketing-share/${share.token}`;
  const expiresOn = (() => {
    try { return new Date(share.expires_at).toLocaleString(); } catch { return share.expires_at; }
  })();
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(url);
      toast.success("Link copied to clipboard.");
    } catch {
      toast.error("Couldn't copy. Select the URL manually.");
    }
  };
  return (
    <div
      className="bg-white border border-[#E5E5E5] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
      data-testid={`marketing-share-row-${share.token}`}
    >
      <div className="flex-shrink-0">
        {share.expired ? (
          <Warning size={20} weight="duotone" className="text-crimson" />
        ) : (
          <CheckCircle size={20} weight="duotone" className="text-[#0F8043]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-ink truncate">{share.label}</p>
        <p className="text-[11px] text-[#6B6B6B] font-mono truncate">{url}</p>
        <p className="text-[10px] text-[#9CA3AF] mt-0.5">
          {share.expired ? "Expired" : "Expires"} {expiresOn} · {share.download_count || 0} downloads
        </p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button size="sm" variant="outline" onClick={copy} className="rounded-full h-8 px-3 text-[12px]" data-testid={`copy-share-${share.token}`}>
          <CopySimple size={12} weight="bold" className="mr-1" /> Copy
        </Button>
        <Button size="sm" variant="outline" onClick={() => onRevoke(share.token)} className="rounded-full h-8 px-3 text-[12px] text-crimson border-[#C1272D]/30 hover:bg-[#C1272D] hover:text-white" data-testid={`revoke-share-${share.token}`}>
          <Trash size={12} weight="bold" />
        </Button>
      </div>
    </div>
  );
}

function ClipCard({ clip, onDownloadClipZip, onDownloadAsset }) {
  const tok = (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  const directMp4 = clip.video?.mp4_path
    ? `${BACKEND}/api/admin/marketing/download?path=${encodeURIComponent(clip.video.mp4_path)}&token=${encodeURIComponent(tok)}`
    : null;
  const fmtSize = (n) => {
    if (!n) return "—";
    const units = ["B", "KB", "MB", "GB"];
    let v = n, i = 0;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
  };
  return (
    <article
      className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden hover:border-[#C9A227] transition-colors"
      data-testid={`clip-card-${clip.id}`}
    >
      {directMp4 && (
        <video
          controls
          preload="metadata"
          src={directMp4}
          className="w-full bg-black aspect-video"
          data-testid={`clip-video-${clip.id}`}
        />
      )}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] text-gold font-bold mb-1">
              Clip · {clip.id}
            </p>
            <h3 className="font-spectral text-lg tracking-tight text-ink truncate">
              {clip.title}
            </h3>
            <p className="text-[11px] text-[#6B6B6B] mt-0.5">{clip.duration_label}</p>
          </div>
        </div>
        <p className="text-[13px] text-[#3F3F3F] leading-relaxed mb-4">
          {clip.summary}
        </p>

        <div className="space-y-3 mb-4">
          <ClipPair
            label="Primary script"
            value={clip.primary_script}
            excerpt={clip.script_excerpt}
            onOpen={() => onDownloadAsset(clip.primary_script)}
          />
          <ClipPair
            label={`Storyboard · section ${clip.storyboard_section}`}
            value={clip.storyboard_label}
            excerpt={clip.storyboard_excerpt}
            onOpen={() => onDownloadAsset("storyboards/all-storyboards.md")}
          />
          <ClipPair
            label="Social caption"
            value={clip.primary_caption_block}
            excerpt={clip.caption_excerpt}
            onOpen={() => onDownloadAsset("captions/social-captions.md")}
          />
          {clip.also_used_in_scripts?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold mb-1">
                Also used in
              </p>
              <ul className="text-[12px] text-[#6B6B6B] space-y-0.5">
                {clip.also_used_in_scripts.map((p) => (
                  <li key={p}>
                    <button
                      type="button"
                      onClick={() => onDownloadAsset(p)}
                      className="hover:text-ink hover:underline truncate text-left font-mono text-[11px]"
                      data-testid={`clip-${clip.id}-also-${p.replace(/[^a-z0-9]+/gi, "-")}`}
                    >
                      {p}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-[#E5E5E5]">
          <Button
            size="sm"
            onClick={onDownloadClipZip}
            data-testid={`clip-${clip.id}-download-zip`}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white h-9 px-4 text-[12px]"
          >
            <DownloadSimple size={13} weight="bold" className="mr-1.5" />
            Download clip folder (.zip)
          </Button>
          {clip.video?.mp4_path && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDownloadAsset(clip.video.mp4_path)}
              data-testid={`clip-${clip.id}-download-mp4`}
              className="rounded-full border-[#E5E5E5] h-9 px-3 text-[12px]"
            >
              .mp4 only · {fmtSize(clip.video.mp4_size)}
            </Button>
          )}
          {clip.video?.webm_path && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDownloadAsset(clip.video.webm_path)}
              data-testid={`clip-${clip.id}-download-webm`}
              className="rounded-full border-[#E5E5E5] h-9 px-3 text-[12px]"
            >
              .webm only · {fmtSize(clip.video.webm_size)}
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}

function ClipPair({ label, value, excerpt, onOpen }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-0.5">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold">
          {label}
        </p>
        <button
          type="button"
          onClick={onOpen}
          className="text-[10px] uppercase tracking-[0.14em] font-bold text-gold hover:text-ink"
        >
          Open →
        </button>
      </div>
      <p className="text-[12px] text-ink truncate" title={value}>{value}</p>
      {excerpt && (
        <p className="text-[11px] text-[#6B6B6B] leading-snug line-clamp-2 mt-0.5">
          {excerpt}
        </p>
      )}
    </div>
  );
}

function _saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * In-platform marketing reel launcher card.
 *
 * Two recording aspect-ratios surfaced — vertical (1080×1920) for IG/TikTok
 * and horizontal (1920×1080) for YouTube/landing autoplay. Opens in a new
 * tab with `?clean=1` already appended so the dimension pill never lands
 * in the captured frame.
 */
function ReelCard({ label, kicker, path }) {
  const [downloadingFor, setDownloadingFor] = useState(null); // null | "vertical" | "horizontal"
  const open = (orientation) => {
    const params = new URLSearchParams({ clean: "1" });
    if (orientation === "horizontal") params.set("o", "horizontal");
    window.open(`${path}?${params.toString()}`, "_blank", "noopener");
  };
  const testid = label.toLowerCase().includes("buyer") ? "reel-card-buyer" : "reel-card-seller";
  const slug = label.toLowerCase().includes("buyer") ? "buyer" : "seller";

  const downloadMp4 = async (orientation) => {
    if (downloadingFor) return; // dedupe — render is single-flight server-side too
    setDownloadingFor(orientation);
    try {
      // First call may block 30-60s while the headless browser walks
      // the timeline and ffmpeg encodes. Subsequent calls are instant
      // off the disk cache. axios `responseType: "blob"` ensures we
      // get the binary payload and not a string.
      const res = await api.get(`/marketing/reels/${slug}.mp4`, {
        params: { o: orientation },
        responseType: "blob",
        timeout: 180_000,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "video/mp4" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `armstrong-k9-reel-${slug}-${orientation}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${slug} reel · ${orientation === "vertical" ? "1080×1920" : "1920×1080"}`);
    } catch (e) {
      toast.error("Reel download failed", {
        description: e?.response?.status === 500
          ? "Server-side render hit an error. Check backend logs."
          : "Please try again — first render takes ~30-60s.",
      });
    } finally {
      setDownloadingFor(null);
    }
  };

  return (
    <div
      className="rounded-xl bg-white border border-[#E2DAC2] p-5 flex flex-col gap-4 shadow-luxury-light"
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <span
          className="flex items-center justify-center rounded-lg flex-shrink-0"
          style={{
            width: 44,
            height: 44,
            background: "#FBF1D8",
            border: "1px solid rgba(201,162,39,0.55)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 2px 8px rgba(201,162,39,0.18)",
          }}
        >
          <FilmStrip size={22} weight="duotone" className="text-gold-dark" />
        </span>
        <div className="min-w-0">
          <p className="font-spectral text-xl tracking-tight">{label}</p>
          <p className="text-[13px] text-[#6B6B6B] mt-0.5">{kicker} · 15-second loop</p>
        </div>
      </div>

      {/* Open in browser — for previewing or screen-recording ad-hoc. */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          onClick={() => open("vertical")}
          className="rounded-full bg-ink hover:bg-crimson text-white h-9 px-4"
          data-testid={`${testid}-open-vertical`}
        >
          <ArrowSquareOut size={14} weight="bold" className="mr-1.5" />
          Open · 1080×1920
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => open("horizontal")}
          className="rounded-full h-9 px-4 border-[#E2DAC2]"
          data-testid={`${testid}-open-horizontal`}
        >
          1920×1080
        </Button>
      </div>

      {/* Download MP4 — server-side render. First call ~30-60s, then
          instant. Two ratios so press / IG / TikTok / YouTube can all
          grab their preferred shape directly. */}
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-dashed border-[#E2DAC2]">
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-gold-dark mr-1">
          Download MP4
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => downloadMp4("vertical")}
          disabled={!!downloadingFor}
          className="rounded-full h-8 px-3 text-[12px] border-gold/55"
          data-testid={`${testid}-download-vertical`}
        >
          {downloadingFor === "vertical"
            ? <span className="inline-flex items-center gap-1.5"><CircleNotch size={12} weight="bold" className="animate-spin" />Rendering…</span>
            : <span className="inline-flex items-center gap-1.5"><DownloadSimple size={12} weight="bold" />1080×1920</span>}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => downloadMp4("horizontal")}
          disabled={!!downloadingFor}
          className="rounded-full h-8 px-3 text-[12px] border-gold/55"
          data-testid={`${testid}-download-horizontal`}
        >
          {downloadingFor === "horizontal"
            ? <span className="inline-flex items-center gap-1.5"><CircleNotch size={12} weight="bold" className="animate-spin" />Rendering…</span>
            : <span className="inline-flex items-center gap-1.5"><DownloadSimple size={12} weight="bold" />1920×1080</span>}
        </Button>
      </div>
    </div>
  );
}

